import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useDataMode } from '../../contexts/DataModeContext';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Inbox, Search, Eye, CheckCircle2, XCircle, Clock,
  Star, ChevronLeft, ChevronRight, RefreshCw, X, Building2,
  MessageSquare, ArrowLeftRight, DollarSign, Loader2, AlertCircle
} from 'lucide-react';import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MOCK_PROPOSALS = [
  { id: 'pr_001', contractor_name: 'Istanbul Fair Solutions Ltd.', contractor_city: 'Istanbul', brief_id: 'br_001', brief_title: 'Istanbul Tech Fair 2025 — Stand Design', amount: 42500, currency: 'TRY', status: 'pending', created_at: '2025-01-08', rating: 4.8, delivery_days: 14, notes: 'Delivery guaranteed within 14 days.' },
  { id: 'pr_002', contractor_name: 'Berlin Stand GmbH', contractor_city: 'Hannover', brief_id: 'br_002', brief_title: 'Hannover Messe 2025 — Modular Stand', amount: 79000, currency: 'EUR', status: 'accepted', created_at: '2025-01-05', rating: 4.9, delivery_days: 21, notes: 'References: BMW, Siemens.' },
  { id: 'pr_003', contractor_name: 'Global Expo Builders', contractor_city: 'Istanbul', brief_id: 'br_001', brief_title: 'Istanbul Tech Fair 2025 — Stand Design', amount: 48000, currency: 'TRY', status: 'counter_offered', counter_amount: 45000, counter_currency: 'TRY', counter_message: 'We can do 45,000 TRY if you can confirm by Friday.', created_at: '2025-01-07', rating: 4.5, delivery_days: 18, notes: 'Fast assembly with modular system.' },
  { id: 'pr_004', contractor_name: 'Dubai Expo Craft LLC', contractor_city: 'Dubai', brief_id: 'br_003', brief_title: 'Dubai Expo Corporate Pavilion', amount: 57000, currency: 'USD', status: 'declined', created_at: '2025-01-02', rating: 4.2, delivery_days: 30, notes: '' },
  { id: 'pr_005', contractor_name: 'Anadolu Stand Co.', contractor_city: 'Izmir', brief_id: 'br_004', brief_title: 'IzFas Organic Food Stand', amount: 18500, currency: 'TRY', status: 'pending', created_at: '2025-01-09', rating: 4.6, delivery_days: 10, notes: 'Sustainable materials throughout.' },
  { id: 'pr_006', contractor_name: 'MedEx Stand Solutions', contractor_city: 'Dubai', brief_id: 'br_005', brief_title: 'Arab Health Dubai 2025', amount: 88000, currency: 'USD', status: 'accepted', created_at: '2024-12-28', rating: 5.0, delivery_days: 25, notes: 'Healthcare sector specialist.' },
];

const STATUS_CONFIG = {
  pending:        { color: 'bg-yellow-100 text-yellow-700 border border-yellow-200',  label: 'Bekliyor',        icon: Clock },
  accepted:       { color: 'bg-emerald-100 text-emerald-700 border border-emerald-200', label: 'Kabul Edildi',  icon: CheckCircle2 },
  declined:       { color: 'bg-red-100 text-red-600 border border-red-200',           label: 'Reddedildi',      icon: XCircle },
  counter_offered:{ color: 'bg-amber-100 text-amber-700 border border-amber-200',     label: 'Karşı Teklif',   icon: ArrowLeftRight },
};

const CURRENCIES = ['EUR', 'USD', 'TRY', 'GBP'];
const PAGE_SIZE = 8;

// ─── Counter Offer Modal ─────────────────────────────────────────────────────
const CounterOfferModal = ({ proposal, onClose, onSent }) => {
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState(proposal.currency || 'EUR');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const { token } = useAuth();
  const { isMockMode } = useDataMode();

  const handleSend = async () => {
    if (!amount || isNaN(Number(amount)) || Number(amount) <= 0) {
      setError('Geçerli bir tutar girin');
      return;
    }
    setSending(true); setError(null);
    try {
      if (isMockMode) {
        await new Promise(r => setTimeout(r, 700));
      } else {
        await axios.post(
          `${API}/exhibitor/proposals/${proposal.id}/counter`,
          { counter_amount: Number(amount), counter_currency: currency, counter_message: message },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      toast.success(`Karşı teklif gönderildi: ${Number(amount).toLocaleString()} ${currency}`);
      onSent();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Gönderilemedi');
    } finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden" data-testid="counter-offer-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-amber-50">
          <div className="flex items-center gap-2">
            <ArrowLeftRight className="h-5 w-5 text-amber-600" />
            <h3 className="font-bold text-gray-900">Karşı Teklif Gönder</h3>
          </div>
          <button onClick={onClose}><X className="h-5 w-5 text-gray-400 hover:text-gray-600" /></button>
        </div>

        <div className="px-6 py-5 space-y-4">
          {/* Current offer info */}
          <div className="bg-gray-50 rounded-xl px-4 py-3 text-sm">
            <p className="text-xs text-gray-400 mb-1">Yüklenicinin Teklifi</p>
            <p className="font-bold text-gray-900">{proposal.contractor_name}</p>
            <p className="text-lg font-black text-gray-800">
              {proposal.amount?.toLocaleString()} <span className="text-sm font-semibold text-gray-500">{proposal.currency}</span>
            </p>
          </div>

          {/* Counter amount */}
          <div>
            <label className="text-xs font-semibold text-gray-600 mb-1.5 block">Karşı Teklif Tutarı <span className="text-red-500">*</span></label>
            <div className="flex gap-2">
              <input
                type="number"
                value={amount}
                onChange={e => setAmount(e.target.value)}
                placeholder="ör. 75000"
                data-testid="counter-amount-input"
                className="flex-1 border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
              />
              <select
                value={currency}
                onChange={e => setCurrency(e.target.value)}
                data-testid="counter-currency-select"
                className="w-24 border border-gray-200 rounded-lg px-2 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 appearance-none bg-white"
              >
                {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>

          {/* Message */}
          <div>
            <label className="text-xs font-semibold text-gray-600 mb-1.5 block">Mesaj <span className="text-gray-400 font-normal">(isteğe bağlı)</span></label>
            <textarea
              value={message}
              onChange={e => setMessage(e.target.value)}
              rows={3}
              data-testid="counter-message-input"
              placeholder="Karşı teklifiniz hakkında kısa bir not..."
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-amber-400"
            />
          </div>

          {error && <p className="text-xs text-red-500 flex items-center gap-1"><AlertCircle className="h-3.5 w-3.5" />{error}</p>}

          <div className="flex gap-2 pt-1">
            <Button onClick={handleSend} disabled={sending} data-testid="submit-counter-offer-btn"
              className="flex-1 bg-amber-500 hover:bg-amber-600 text-white gap-2">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowLeftRight className="h-4 w-4" />}
              {sending ? 'Gönderiliyor...' : 'Karşı Teklif Gönder'}
            </Button>
            <Button variant="outline" onClick={onClose} className="px-4">İptal</Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const ExhibitorProposals = () => {
  const { isMockMode } = useDataMode();
  const { user, token } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const briefFilter = searchParams.get('brief') || '';
  const myOrgId = user?.org_memberships?.[0]?.org_id || '';

  const [proposals, setProposals] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(null);
  const [counterTarget, setCounterTarget] = useState(null);
  const [actioning, setActioning] = useState(null);

  const handleMessage = async (p) => {
    const contractorOrgId = p.contractor_org_id || '';
    if (!myOrgId || !contractorOrgId) return;
    try {
      const res = await axios.post(
        `${API}/messages/conversations`,
        { brief_id: p.brief_id || '', proposal_id: p.id, exhibitor_org_id: myOrgId, contractor_org_id: contractorOrgId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      navigate(`/app/exhibitor/messages?conv=${res.data.conversation_id}`);
    } catch { navigate('/app/exhibitor/messages'); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (isMockMode) {
        await new Promise(r => setTimeout(r, 400));
        let filtered = MOCK_PROPOSALS;
        if (statusFilter !== 'all') filtered = filtered.filter(p => p.status === statusFilter);
        if (search) filtered = filtered.filter(p =>
          p.contractor_name.toLowerCase().includes(search.toLowerCase()) ||
          p.brief_title.toLowerCase().includes(search.toLowerCase())
        );
        setTotal(filtered.length);
        setProposals(filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE));
      } else {
        const params = { skip: (page - 1) * PAGE_SIZE, limit: PAGE_SIZE };
        if (statusFilter !== 'all') params.status = statusFilter;
        const res = await axios.get(`${API}/exhibitor/proposals`, {
          headers: { Authorization: `Bearer ${token}` }, params,
        });
        const items = (res.data.items || []).map(p => ({
          id: p.id,
          brief_id: p.brief_id,
          contractor_org_id: p.contractor_org_id,
          brief_title: p.brief_title || p.brief_event?.show || p.brief_id,
          contractor_name: p.contractor_name || p.contractor_overview?.name || p.contractor_org_id,
          contractor_city: p.contractor_overview?.city || '',
          amount: p.amount,
          currency: p.currency,
          status: p.status,
          created_at: p.created_at,
          rating: p.contractor_overview?.rating,
          delivery_days: p.delivery_days,
          notes: p.notes,
          counter_amount: p.counter_amount,
          counter_currency: p.counter_currency,
          counter_message: p.counter_message,
          counter_sent_by: p.counter_sent_by,
        }));
        setProposals(items);
        setTotal(res.data.total || items.length);
      }
    } catch { setProposals([]); }
    finally { setLoading(false); }
  }, [isMockMode, token, page, statusFilter, search]);

  useEffect(() => { load(); }, [load]);

  const handleAction = async (proposalId, newStatus) => {
    setActioning(proposalId);
    try {
      if (!isMockMode) {
        await axios.patch(
          `${API}/exhibitor/proposals/${proposalId}`,
          { status: newStatus },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        await new Promise(r => setTimeout(r, 500));
      }
      toast.success(newStatus === 'accepted' ? 'Teklif kabul edildi!' : 'Teklif reddedildi');
      setSelected(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'İşlem başarısız');
    } finally { setActioning(null); }
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  // If briefFilter is active (from URL), filter client-side by brief_id
  const displayedProposals = briefFilter
    ? proposals.filter(p => p.brief_id === briefFilter)
    : proposals;

  const kpis = [
    { label: 'Toplam Teklif', value: total, color: 'bg-blue-50 text-blue-700' },
    { label: 'Bekleyen', value: proposals.filter(p => p.status === 'pending').length, color: 'bg-yellow-50 text-yellow-700' },
    { label: 'Kabul Edildi', value: proposals.filter(p => p.status === 'accepted').length, color: 'bg-emerald-50 text-emerald-700' },
    { label: 'Karşı Teklif', value: proposals.filter(p => p.status === 'counter_offered').length, color: 'bg-amber-50 text-amber-700' },
  ];

  return (
    <div className="space-y-5" data-testid="exhibitor-proposals-page">
      {/* Counter-offer modal */}
      {counterTarget && (
        <CounterOfferModal
          proposal={counterTarget}
          onClose={() => setCounterTarget(null)}
          onSent={async () => { setCounterTarget(null); setSelected(null); await load(); }}
        />
      )}

      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-black text-gray-900">Teklifler</h1>
          <p className="text-sm text-gray-500">Yüklenicilerden gelen teklifleri yönetin</p>
        </div>
        <div className="flex items-center gap-2">
          {briefFilter && (
            <div className="flex items-center gap-1.5 bg-orange-50 border border-orange-200 rounded-full px-3 py-1">
              <span className="text-xs font-semibold text-orange-700">Brief Filtresi Aktif</span>
              <button onClick={() => navigate('/app/exhibitor/proposals')}
                className="text-orange-400 hover:text-orange-600 transition-colors ml-1" title="Filtreyi Kaldır">
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
          <Button onClick={load} variant="outline" size="sm" className="gap-1.5">
            <RefreshCw className="h-3.5 w-3.5" />Yenile
          </Button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {kpis.map(k => (
          <div key={k.label} className={`rounded-xl px-4 py-3 ${k.color}`}>
            <p className="text-2xl font-black leading-none">{k.value}</p>
            <p className="text-xs font-medium mt-0.5 opacity-80">{k.label}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-52">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
            placeholder="Yüklenici veya brief ara..." data-testid="proposals-search"
            className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>
        <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
          data-testid="status-filter-select"
          className="border border-gray-200 rounded-xl px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500">
          <option value="all">Tüm Durumlar</option>
          <option value="pending">Bekleyen</option>
          <option value="counter_offered">Karşı Teklif</option>
          <option value="accepted">Kabul</option>
          <option value="declined">Reddedildi</option>
        </select>
      </div>

      {/* List */}
      {loading ? (
        <div className="space-y-2">{[...Array(4)].map((_, i) => <div key={i} className="h-16 bg-gray-100 animate-pulse rounded-xl" />)}</div>
      ) : displayedProposals.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <Inbox className="h-12 w-12 mx-auto mb-3 opacity-30" />
          <p className="text-sm font-medium">{briefFilter ? 'Bu brief için teklif bulunamadı' : 'Teklif bulunamadı'}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {displayedProposals.map(p => {
            const sc = STATUS_CONFIG[p.status] || STATUS_CONFIG.pending;
            const SIcon = sc.icon;
            const isCounterOffered = p.status === 'counter_offered';
            return (
              <div key={p.id} data-testid={`proposal-row-${p.id}`}
                className={`flex items-center gap-3 bg-white rounded-xl border px-4 py-3.5 hover:border-blue-200 hover:shadow-sm transition-all ${isCounterOffered ? 'border-amber-300 bg-amber-50/30' : 'border-gray-100'}`}>
                <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center text-blue-700 font-bold text-base shrink-0">
                  {(p.contractor_name || '?').charAt(0)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-gray-900 truncate">{p.contractor_name}</p>
                  <p className="text-xs text-gray-400 truncate">{p.brief_title}</p>
                  {isCounterOffered && p.counter_amount && (
                    <p className="text-xs text-amber-700 font-semibold mt-0.5 flex items-center gap-1">
                      <ArrowLeftRight className="h-3 w-3" />
                      Karşı teklif: {p.counter_amount?.toLocaleString()} {p.counter_currency}
                    </p>
                  )}
                </div>
                <div className="hidden sm:flex flex-col items-end shrink-0">
                  <p className="text-sm font-bold text-gray-900">{p.amount?.toLocaleString()} {p.currency}</p>
                  <p className="text-xs text-gray-400">{p.delivery_days && `${p.delivery_days} gün`}</p>
                </div>
                <span className={`text-xs font-semibold px-2.5 py-1 rounded-full flex items-center gap-1 shrink-0 ${sc.color}`}>
                  <SIcon className="h-3 w-3" />{sc.label}
                </span>
                <div className="flex items-center gap-1.5 shrink-0">
                  <button onClick={() => setSelected(p)} data-testid={`view-proposal-${p.id}`}
                    className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                    <Eye className="h-4 w-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button onClick={() => setPage(p => p - 1)} disabled={page <= 1}
            className="p-2 border border-gray-200 rounded-lg disabled:opacity-30 hover:bg-gray-50">
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="text-sm text-gray-600 px-2">{page} / {totalPages}</span>
          <button onClick={() => setPage(p => p + 1)} disabled={page >= totalPages}
            className="p-2 border border-gray-200 rounded-lg disabled:opacity-30 hover:bg-gray-50">
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Detail Drawer */}
      {selected && (
        <div className="fixed inset-0 bg-black/40 z-40 flex items-center justify-center p-4" onClick={e => e.target === e.currentTarget && setSelected(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden" data-testid="proposal-detail-modal">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-blue-100 flex items-center justify-center text-blue-700 font-bold">
                  {(selected.contractor_name || '?').charAt(0)}
                </div>
                <div>
                  <p className="font-bold text-sm text-gray-900">{selected.contractor_name}</p>
                  <p className="text-xs text-gray-400">{selected.contractor_city}</p>
                </div>
              </div>
              <button onClick={() => setSelected(null)}><X className="h-5 w-5 text-gray-400 hover:text-gray-600" /></button>
            </div>

            <div className="px-6 py-5 space-y-4 overflow-y-auto max-h-[70vh]">
              {/* Brief */}
              <div className="bg-gray-50 rounded-xl p-4 text-sm">
                <p className="text-xs text-gray-400 mb-1 uppercase tracking-wide">Brief</p>
                <p className="font-semibold text-gray-800">{selected.brief_title}</p>
              </div>

              {/* Offer */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-blue-50 rounded-xl p-4">
                  <p className="text-xs text-blue-500 mb-1">Teklif Tutarı</p>
                  <p className="text-xl font-black text-blue-800">{selected.amount?.toLocaleString()}</p>
                  <p className="text-sm text-blue-600 font-semibold">{selected.currency}</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-xs text-gray-400 mb-1">Teslimat</p>
                  <p className="text-xl font-black text-gray-800">{selected.delivery_days || '—'}</p>
                  <p className="text-sm text-gray-500">gün</p>
                </div>
              </div>

              {/* Counter offer info */}
              {selected.status === 'counter_offered' && selected.counter_amount && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <ArrowLeftRight className="h-4 w-4 text-amber-600" />
                    <p className="text-sm font-bold text-amber-800">
                      {selected.counter_sent_by === 'contractor' ? 'Yüklenicinin Karşı Teklifi' : 'Sizin Karşı Teklifiniz'}
                    </p>
                  </div>
                  <p className="text-xl font-black text-amber-700">{selected.counter_amount?.toLocaleString()} {selected.counter_currency}</p>
                  {selected.counter_message && <p className="text-xs text-amber-600 mt-2 italic">"{selected.counter_message}"</p>}
                </div>
              )}

              {/* Rating */}
              {selected.rating && (
                <div className="flex items-center gap-1.5 text-sm">
                  <Star className="h-4 w-4 text-amber-400 fill-amber-400" />
                  <span className="font-semibold text-gray-800">{selected.rating}</span>
                  <span className="text-gray-400">/ 5.0 genel puan</span>
                </div>
              )}

              {/* Notes */}
              {selected.notes && (
                <div className="bg-gray-50 rounded-xl p-4 text-sm text-gray-700">
                  <p className="text-xs text-gray-400 mb-1 uppercase tracking-wide">Not</p>
                  <p>{selected.notes}</p>
                </div>
              )}

              {/* Action buttons */}
              {(selected.status === 'pending' || selected.status === 'counter_offered') && (
                <div className="flex flex-col gap-2 pt-2">
                  <div className="flex gap-2">
                    <Button onClick={() => handleAction(selected.id, 'accepted')}
                      disabled={actioning === selected.id} data-testid="accept-proposal-btn"
                      className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white gap-2">
                      {actioning === selected.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                      Kabul Et
                    </Button>
                    <Button onClick={() => handleAction(selected.id, 'declined')}
                      disabled={actioning === selected.id} data-testid="decline-proposal-btn"
                      variant="outline" className="flex-1 border-red-200 text-red-600 hover:bg-red-50 gap-2">
                      <XCircle className="h-4 w-4" />Reddet
                    </Button>
                  </div>
                  <Button onClick={() => setCounterTarget(selected)} data-testid="counter-offer-btn"
                    variant="outline" className="w-full border-amber-300 text-amber-700 hover:bg-amber-50 gap-2">
                    <ArrowLeftRight className="h-4 w-4" />Karşı Teklif Gönder
                  </Button>
                  <Button onClick={() => handleMessage(selected)} variant="ghost" className="w-full gap-2 text-gray-500">
                    <MessageSquare className="h-4 w-4" />Mesaj Gönder
                  </Button>
                </div>
              )}

              {selected.status === 'accepted' && (
                <div className="flex items-center gap-2 bg-emerald-50 rounded-xl px-4 py-3">
                  <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                  <p className="text-sm font-semibold text-emerald-800">Bu teklif kabul edilmiş.</p>
                  <Button onClick={() => handleMessage(selected)} size="sm" variant="ghost" className="ml-auto gap-1 text-emerald-700">
                    <MessageSquare className="h-3.5 w-3.5" />Mesaj
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExhibitorProposals;
